import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';
import { publishSnapshot } from '../hooks/useToday';

/** IANA-пояс браузера (Europe/Moscow); null, если Intl его не знает. */
const userTimeZone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (_) { return null; }
};

// Пояс пользователя — в каждом запросе: «сегодня», цель дня и серия считаются на сервере в нём.
// Ставим в defaults при загрузке модуля, чтобы его получил и самый первый /auth/me.
const TZ = userTimeZone();
if (TZ) axios.defaults.headers.common['X-Timezone'] = TZ;

/** /auth/me несёт тот же блок «Сегодня», что и /dashboard, — сразу кладём в общий кэш. */
const publishTodayFromUser = (u) => {
  if (u && u.today) publishSnapshot({ today: u.today, user: { streak: u.streak } });
};

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Всегда подставляем токен в заголовок при его наличии (важно после обновления страницы)
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
    }
  }, [token]);

  // Страховка: если за 12 сек загрузка не завершилась — показываем экран (логин)
  useEffect(() => {
    const t = setTimeout(() => {
      setLoading((prev) => (prev ? false : prev));
    }, 12000);
    return () => clearTimeout(t);
  }, []);

  // Перехватчик: подставлять токен в каждый запрос из localStorage (на случай гонки после refresh)
  useEffect(() => {
    const requestId = axios.interceptors.request.use((config) => {
      const t = localStorage.getItem('token');
      if (t) config.headers.Authorization = config.headers.Authorization || `Bearer ${t}`;
      return config;
    });
    return () => axios.interceptors.request.eject(requestId);
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get('/api/auth/me', { timeout: 10000 });
      setUser(response.data.user);
      publishTodayFromUser(response.data.user);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error('[AuthContext] Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const getErrorMessage = (error, fallback) => {
    if (!error) return fallback;
    const data = error.response?.data;
    const status = error.response?.status;
    const isGenericAxiosMessage = (s) => typeof s === 'string' && /^Request failed with status code \d+/i.test(s.trim());
    let msg = null;
    if (data && typeof data === 'object') {
      if (typeof data.message === 'string' && data.message.trim()) msg = data.message.trim();
      else if (typeof data.error === 'string' && data.error.trim()) msg = data.error.trim();
    } else if (typeof data === 'string' && data.trim()) {
      msg = data.trim().length > 200 ? data.trim().slice(0, 200) + '…' : data.trim();
    }
    if (msg && !isGenericAxiosMessage(msg)) return msg;
    if (error.message && !isGenericAxiosMessage(error.message)) return error.message;
    if (status === 400) return 'Проверьте введённые данные и попробуйте снова.';
    if (status === 401) return 'Неверный логин или пароль.';
    if (status >= 500) return 'Ошибка сервера. Проверьте, что backend запущен (порт 5001), посмотрите логи в терминале и попробуйте позже.';
    if (error.request) return 'Не удалось подключиться к серверу. Проверьте, что backend запущен.';
    return fallback;
  };

  const register = async (username, password, email = null) => {
    const trimmedUsername = typeof username === 'string' ? username.trim() : '';
    const trimmedEmail = email && String(email).trim() ? String(email).trim() : null;
    if (!trimmedUsername || !password) {
      return { success: false, message: 'Логин и пароль обязательны' };
    }
    try {
      const response = await axios.post('/api/auth/register', {
        username: trimmedUsername,
        password: String(password),
        ...(trimmedEmail && { email: trimmedEmail }),
        role: 'user'
      });
      if (!response.data?.token) {
        return { success: false, message: 'Неверный формат ответа от сервера' };
      }
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      let message = getErrorMessage(error, 'Ошибка регистрации');
      if (/uv_interface|Unknown system error/i.test(message)) {
        message = 'Временная ошибка сервера. Убедитесь, что backend запущен в обычном терминале (не в песочнице) и попробуйте снова.';
      }
      return { success: false, message };
    }
  };

  const login = async (username, password) => {
    const u = typeof username === 'string' ? username.trim() : '';
    const p = password != null ? String(password) : '';
    if (!u || !p) {
      return { success: false, message: 'Логин и пароль обязательны' };
    }
    try {
      const response = await axios.post('/api/auth/login', { username: u, password: p });
      if (!response.data?.token) {
        return { success: false, message: 'Неверный формат ответа от сервера' };
      }
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      setToken(token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      return { success: true };
    } catch (error) {
      const errorMessage = getErrorMessage(error, 'Неверный логин или пароль');
      return { success: false, message: errorMessage };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    delete axios.defaults.headers.common['Authorization'];
  };

  const logoutRef = useRef(logout);
  logoutRef.current = logout;

  // При 401 или 400 на запросах к словам/предложениям — выходим, чтобы пользователь мог войти снова
  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (err) => {
        const status = err.response?.status;
        const url = err.config?.url || '';
        if (status === 401) {
          logoutRef.current();
        } else if (status === 400 && typeof url === 'string' && /\/api\/(words|sentences)(\/|$|\?)/.test(url)) {
          // 400 на слова/предложения часто из-за сессии — принудительный выход
          logoutRef.current();
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  const forgotPassword = async (email) => {
    try {
      const response = await axios.post('/api/auth/forgot-password', { email: String(email).trim() });
      return { success: true, message: response.data?.message || 'Письмо отправлено' };
    } catch (error) {
      return { success: false, message: getErrorMessage(error, 'Ошибка запроса') };
    }
  };

  const resetPassword = async (token, newPassword) => {
    try {
      const response = await axios.post('/api/auth/reset-password', { token, newPassword });
      return { success: true, message: response.data?.message || 'Пароль изменён' };
    } catch (error) {
      return { success: false, message: getErrorMessage(error, 'Ошибка сброса пароля') };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
      publishTodayFromUser(response.data.user);
      return response.data.user;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  };

  const value = {
    user,
    login,
    register,
    logout,
    forgotPassword,
    resetPassword,
    loading,
    isAuthenticated: !!user,
    refreshUser
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
